/**
 * WidgetHeaderActions — [T-DASHBOARD-VISIBLE-HEADER] gear dropdown for widgets.
 *
 * Extracted from `WidgetCardChrome.tsx` (the v3.2 floating chrome). The v4
 * redesign moves the chrome from `absolute left-2 right-2 top-2 z-20` (a
 * floating overlay that overlapped widget data) into a proper flex row
 * inside the widget cell (driven by `HeaderSlot`). This component owns
 * ONLY the dropdown portion of that chrome — the gear button trigger +
 * the sectioned Radix DropdownMenu.
 *
 * ## Behaviour
 *
 * - Renders nothing in preview mode (no gear button, no dropdown).
 * - Renders nothing when called outside a `WidgetContext.Provider`.
 * - In edit mode, renders the same gear button + sectioned dropdown as the
 *   v3.2 `WidgetCardChrome`, with all 13 testids preserved.
 *
 * ## Testids (all 13 preserved from WidgetCardChrome for backwards compat)
 *
 * | testid                          | element                              |
 * |---------------------------------|--------------------------------------|
 * | `widget-card-gear`              | gear button (DropdownMenu trigger)   |
 * | `widget-inline-toolbar`         | DropdownMenuContent                  |
 * | `widget-inline-toolbar-header`  | dropdown header showing widget title |
 * | `widget-inline-configure-btn`   | Configure menu item                  |
 * | `widget-inline-duplicate-btn`   | Duplicate menu item                  |
 * | `widget-inline-replace-btn`     | Replace menu item                    |
 * | `widget-inline-hide-btn`        | Hide menu item                       |
 * | `widget-inline-narrow-btn`      | Narrow menu item                     |
 * | `widget-inline-widen-btn`       | Widen menu item                      |
 * | `widget-inline-remove-btn`      | Remove menu item                     |
 * | `widget-inline-delete-btn`      | Delete menu item (destructive)       |
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dropdown-menu
 */
import {
  ArrowLeftRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  EyeOff,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useWidgetContext } from './WidgetContext';

/**
 * Gear button + sectioned dropdown. Reads mode + actions + title from
 * `WidgetContext`. Returns `null` in preview mode or when no provider
 * is mounted.
 */
export function WidgetHeaderActions() {
  const ctx = useWidgetContext();
  if (!ctx || ctx.mode === 'preview') return null;
  const { actions, title } = ctx;

  // Build a local alias for readability — preserves the variable shape
  // the v3.2 chrome used (handler variables, not destructured on each use).
  const {
    onConfigure,
    onDuplicate,
    onReplace,
    onHide,
    onResizeNarrow,
    onResizeWiden,
    onRemove,
    onDelete,
  } = actions;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="widget-card-gear"
          aria-label={`Widget actions for ${title}`}
          className={cn(
            'shrink-0 flex items-center justify-center rounded-md',
            // 44px touch target on touch devices, 28px on desktop
            'h-11 w-11 md:h-7 md:w-7',
            'text-muted-foreground hover:text-foreground hover:bg-accent/60',
            'data-[state=open]:bg-accent data-[state=open]:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'transition-colors',
          )}
        >
          <Settings className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        data-testid="widget-inline-toolbar"
        data-widget-action-overflow-guard="enabled"
        align="end"
        side="bottom"
        sideOffset={6}
        className="min-w-[14rem] z-50 p-1"
      >
        {/* Header — widget name. Provides context for which widget the
            actions apply to. */}
        <div className="px-3 py-2 border-b border-border/40 mb-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Widget
          </div>
          <div
            className="text-sm font-medium truncate"
            data-testid="widget-inline-toolbar-header"
          >
            {title}
          </div>
        </div>

        {/* ── Widget section ────────────────────────────────────────────── */}
        {onConfigure && (
          <DropdownMenuItem
            data-testid="widget-inline-configure-btn"
            onSelect={onConfigure}
            className="px-3 py-2"
          >
            <Settings
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Configure</span>
          </DropdownMenuItem>
        )}
        {onDuplicate && (
          <DropdownMenuItem
            data-testid="widget-inline-duplicate-btn"
            onSelect={onDuplicate}
            className="px-3 py-2"
          >
            <Copy
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Duplicate</span>
          </DropdownMenuItem>
        )}
        {onReplace && (
          <DropdownMenuItem
            data-testid="widget-inline-replace-btn"
            onSelect={onReplace}
            className="px-3 py-2"
          >
            <ArrowLeftRight
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Replace</span>
          </DropdownMenuItem>
        )}
        {onHide && (
          <DropdownMenuItem
            data-testid="widget-inline-hide-btn"
            onSelect={onHide}
            className="px-3 py-2"
          >
            <EyeOff
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Hide</span>
          </DropdownMenuItem>
        )}

        {/* ── Layout section ────────────────────────────────────────────── */}
        {(onResizeNarrow || onResizeWiden) && (
          <>
            {(onConfigure || onDuplicate || onReplace || onHide) && (
              <DropdownMenuSeparator />
            )}
            <DropdownMenuLabel className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Layout
            </DropdownMenuLabel>
          </>
        )}
        {onResizeNarrow && (
          <DropdownMenuItem
            data-testid="widget-inline-narrow-btn"
            onSelect={onResizeNarrow}
            className="px-3 py-2"
          >
            <ChevronsLeft
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Narrow</span>
          </DropdownMenuItem>
        )}
        {onResizeWiden && (
          <DropdownMenuItem
            data-testid="widget-inline-widen-btn"
            onSelect={onResizeWiden}
            className="px-3 py-2"
          >
            <ChevronsRight
              className="mr-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Widen</span>
          </DropdownMenuItem>
        )}

        {/* ── Remove section (destructive) ─────────────────────────────── */}
        {(onRemove || onDelete) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Remove
            </DropdownMenuLabel>
          </>
        )}
        {onRemove && (
          <DropdownMenuItem
            data-testid="widget-inline-remove-btn"
            onSelect={onRemove}
            className="px-3 py-2 focus:bg-destructive/10 focus:text-destructive"
          >
            <X className="mr-2.5 h-4 w-4" aria-hidden="true" />
            <span>Remove from dashboard</span>
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            data-testid="widget-inline-delete-btn"
            onSelect={onDelete}
            className="px-3 py-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <Trash2 className="mr-2.5 h-4 w-4" aria-hidden="true" />
            <span>Delete permanently</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
